import { useScreenSize } from "hooks/useScreenSize";
import cx from "classnames";
import { FC, useCallback, useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { SliderArrow } from "./SliderArrow";
import { Image, ResponsiveImageType } from "react-datocms/image";
import { Product3dIconSvg } from "components/icons/Product3dIconSvg";
import { useRouter } from "next/router";

const MAX_SLIDES_TO_SHOW = 5;

type ImageSliderProps = {
  items: ResponsiveImageType[];
  setSelectedImage: (index: number) => void;
  selectedImage: number;
  title: string;
  threeDImage?: ResponsiveImageType;
  setImageType: (imageType: "image" | "3d") => void;
};

export const ImageSlider: FC<ImageSliderProps> = ({
  items,
  setSelectedImage,
  selectedImage,
  title,
  threeDImage,
  setImageType,
}) => {
  const router = useRouter();
  const { isMobile } = useScreenSize();
  const sliderRef = useRef<Slider>(null);

  // include the 3D image thumbnail in our count, if applicable
  const totalSlides = items.length + (threeDImage ? 1 : 0);

  useEffect(() => {
    // when the page route changes (color selection), selected slide needs to be reset
    setTimeout(() => {
      if (sliderRef.current && selectedImage >= 0) {
        sliderRef.current.slickGoTo(selectedImage);
      }
    }, 500);
  }, [router.query, selectedImage]);

  const [dragging, setDragging] = useState(false);

  const handleBeforeChange = useCallback(() => {
    setDragging(true);
  }, [setDragging]);

  const handleAfterChange = useCallback(() => {
    setDragging(false);
  }, [setDragging]);

  const settings = {
    speed: 500,
    infinite: false,
    slidesToShow:
      totalSlides < MAX_SLIDES_TO_SHOW ? totalSlides : MAX_SLIDES_TO_SHOW,
    slidesToScroll: 1,
    arrows: totalSlides <= MAX_SLIDES_TO_SHOW ? false : !isMobile,
    prevArrow: <SliderArrow isLeft />,
    nextArrow: <SliderArrow />,
    beforeChange: handleBeforeChange,
    afterChange: handleAfterChange,
  };

  return (
    <div>
      <Slider {...settings} ref={sliderRef}>
        {threeDImage && (
          <div
            key={0}
            bi-type={`${title} pdp - Image gallery`}
            bi-id={`thumbnail - img${0}`}
          >
            <div className="p-1 flex justify-center">
              <div
                className={cx(
                  "w-[82px] relative cursor-pointer overflow-hidden bg-white rounded-[8px] md:rounded-[18px]",
                  selectedImage === -1 && "outline outline-2",
                )}
                onClick={() => {
                  if (!dragging) {
                    setImageType("3d");
                    setSelectedImage(-1);
                  }
                }}
              >
                <Image
                  objectFit="contain"
                  layout="responsive"
                  pictureClassName="w-full aspect-square"
                  data={threeDImage}
                />
                <div className="absolute inset-0 bg-primary bg-opacity-50 z-10 flex justify-center items-center bg-opacity-80">
                  <Product3dIconSvg />
                </div>
              </div>
            </div>
          </div>
        )}

        {items.map((item, idx) => (
          <div
            key={idx + 1}
            bi-type={`${title} pdp - Image gallery`}
            bi-id={`thumbnail - img${idx + 1}`}
          >
            <div className="p-1 flex justify-center">
              <div
                className={cx(
                  "w-[82px] relative cursor-pointer overflow-hidden bg-white rounded-[8px] md:rounded-[18px]",
                  idx === selectedImage && "outline outline-2",
                )}
                onClick={() => {
                  if (!dragging) {
                    setImageType("image");
                    setSelectedImage(idx);
                  }
                }}
              >
                <Image
                  objectFit="contain"
                  layout="responsive"
                  pictureClassName="w-full aspect-square"
                  data={item}
                />
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
};
